import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography, Switch, FormControlLabel, Checkbox, IconButton, Popover, Paper } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CustomTextField from '../../CustomTextField';
import CustomDatePicker from '../../CustomDatePicker';
import CustomDropDown from '../../CustomDropDown';
import MultiSelectDropDown from '../../CustomComponents/MultiSelectDropDown';
import Loader from '../../Loader';
import AssessmentConfiguration from './AssessmentConfiguration';
import { headingBlue, red, mediumGrey, lightGrey } from '../../../assets/colors';
import { buttonStyle, errorTextStyle } from '../../../utils/commonCSS';
import HttpService from '../../../backend/requestApis';
import { updateTrainingBatch, fetchBatchCoordinators, fetchTrainingProgramsList, fetchWORKerByCateogary } from '../../../backend/backendApis';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

const EditBatchDialog = (props) => {
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [programName, setProgramName] = useState('');
  const [programId, setProgramId] = useState('');
  
  const [coordinatorList, setCoordinatorList] = useState([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState('Select Coordinator *');
  const [selectedCoordinatorContact, setSelectedCoordinatorContact] = useState('');
  const [coordinatorError, setCoordinatorError] = useState('');
  
  const [step, setStep] = useState(1);
  const [assessmentInitialValues, setAssessmentInitialValues] = useState(null);

  const [selectedDays, setSelectedDays] = useState([]);
  const [daysError, setDaysError] = useState('');

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeLeftItems, setActiveLeftItems] = useState([]);
  const [activeRightItems, setActiveRightItems] = useState([]);
  
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedGender, setSelectedGender] = useState('All');
  const [statesList, setStatesList] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [availableSearch, setAvailableSearch] = useState('');
  const [initialMemberIds, setInitialMemberIds] = useState([]);
  
  const [selectedProgramApplicableFor, setSelectedProgramApplicableFor] = useState([]);

  const [isLoader, setLoader] = useState(false);
  const [apiError, setApiError] = useState('');

  const selectedItemsRef = useRef([]);
  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (props.dialogStatus && props.editData) {
      setIsEditMode(false);
      setBatchName(props.editData.batchName || '');
      setStartDate(props.editData.startDate ? dayjs(props.editData.startDate) : null);
      setEndDate(props.editData.endDate ? dayjs(props.editData.endDate) : null);
      setProgramName(props.editData.programName || '');
      setProgramId(props.editData.programId || '');
      
      setSelectedCoordinator(props.editData.volunteerName && props.editData.volunteerCN ? `${props.editData.volunteerName} - ${props.editData.volunteerCN}` : 'Select Coordinator *');
      setSelectedCoordinatorContact(props.editData.volunteerCN || '');
      
      setSelectedDays(Array.isArray(props.editData.classFrequency) ? props.editData.classFrequency : []);
      
      if (props.editData.members && Array.isArray(props.editData.members) && typeof props.editData.members[0] === 'object') {
        setSelectedItems(props.editData.members.map(m => ({...m, id: m._id || m.id || m.contactNumber})));
      } else {
        setSelectedItems([]);
      }
      
      setActiveLeftItems([]);
      setActiveRightItems([]);
      setFilterAnchorEl(null);
      setSelectedGender('All');
      setStatesList([]);
      setSelectedStates([]);
      setDistrictsList([]);
      setSelectedDistricts([]);
      setAvailableSearch('');
      setInitialMemberIds([]);
      setApiError('');
      setStep(1);
      
      fetchInitialData(props.editData.programId || props.editData.program);
      
      if (props.editData.assessmentConfig) {
          setAssessmentInitialValues({
              isAssessmentRequired: true,
              easyQty: props.editData.assessmentConfig.easyQuestions?.count || 0,
              easyCredit: props.editData.assessmentConfig.easyQuestions?.credit || 0,
              mediumQty: props.editData.assessmentConfig.mediumQuestions?.count || 0,
              mediumCredit: props.editData.assessmentConfig.mediumQuestions?.credit || 0,
              hardQty: props.editData.assessmentConfig.hardQuestions?.count || 0,
              hardCredit: props.editData.assessmentConfig.hardQuestions?.credit || 0,
              qualPercent: props.editData.assessmentConfig.qualificationPercentage || 0,
              time: props.editData.assessmentConfig.time || 30,
              genCert: props.editData.assessmentConfig.actionOnQualification?.generateCertificate || false,
              changeStatus: props.editData.assessmentConfig.actionOnQualification?.changeWorkerStatus || false,
              workerCategory: props.editData.assessmentConfig.actionOnQualification?.workerCategory || ''
          });
      } else {
          setAssessmentInitialValues({
              isAssessmentRequired: false,
              easyQty: 6, easyCredit: 1, mediumQty: 3, mediumCredit: 3, hardQty: 2, hardCredit: 5, qualPercent: 75, time: 30, genCert: false, changeStatus: false, workerCategory: ''
          });
      }
    }
  }, [props.dialogStatus, props.editData]);

  const fetchInitialData = async (progId) => {
    setLoader(true);
    try {
      let token = localStorage.getItem('token');
      let progRes = await HttpService({
        url: fetchTrainingProgramsList,
        method: 'GET',
        headers: { Authorization: token ? `Bearer ${token}` : null }
      });
      let programs = progRes?.data?.data || [];
      let currentProg = programs.find(p => p._id === progId || p.id === progId || p.uniqueId === progId || p.programName === programName);
      
      if (currentProg) {
          setProgramName(currentProg.programName);
      }
      
      let applicableFor = currentProg ? currentProg.programApplicableFor : [];
      setSelectedProgramApplicableFor(applicableFor);

      if (progId || currentProg) {
        let validProgId = progId || currentProg._id || currentProg.id;
        let coordRes = await HttpService({
          url: fetchBatchCoordinators,
          method: 'GET',
          params: { 
            programId: validProgId,
            batchId: props.editData._id || props.editData.batchId || props.editData.id 
          },
          headers: { Authorization: token ? `Bearer ${token}` : null }
        });
        let fetchedWorkers = coordRes?.data?.data || [];
        setCoordinatorList(fetchedWorkers.map(worker => ({
          name: `${worker.name} - ${worker.contactNumber}`,
          value: worker.contactNumber
        })));
      }

      let options = {
        url: fetchWORKerByCateogary,
        method: 'GET',
        params: {
          workerTypes: applicableFor,
          search: ''
        },
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      };
      let resp = await HttpService(options);
      let fetchedItems = resp?.data?.data || [];
      let formattedList = fetchedItems.map(worker => ({
          ...worker,
          id: worker._id || worker.contactNumber
      }));
      
      let initialSelected = [];
      if (props.editData && props.editData.members && Array.isArray(props.editData.members)) {
          if (typeof props.editData.members[0] === 'object') {
              initialSelected = props.editData.members.map(m => ({...m, id: m._id || m.id || m.contactNumber}));
          } else {
              initialSelected = formattedList.filter(w => props.editData.members.includes(w.id) || props.editData.members.includes(w.contactNumber));
          }
      }
      setSelectedItems(initialSelected);
      setInitialMemberIds(initialSelected.map(m => m.id));
      
      let filteredList = formattedList.filter(
          (item) => !initialSelected.some((sItem) => sItem.id === item.id)
      );
      setAvailableItems(filteredList);
      
    } catch (err) {
      if (err?.status === 403) {
        localStorage.clear();
        navigate('/Login');
      }
      console.log('Error fetching initial data: ', err);
    }
    setLoader(false);
  };

  const fetchAvailableWorkers = async (searchValue = '', workerTypes = selectedProgramApplicableFor) => {
    try {
      let options = {
        url: fetchWORKerByCateogary,
        method: 'GET',
        params: {
          workerTypes: workerTypes,
          search: searchValue
        },
        headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' }
      };
      let resp = await HttpService(options);
      let fetchedItems = resp?.data?.data || [];
      let formattedList = fetchedItems.map(worker => ({
          ...worker,
          id: worker._id || worker.contactNumber
      }));
      let filteredList = formattedList.filter(
          (item) => !selectedItemsRef.current.some((sItem) => sItem.id === item.id)
      );
      setAvailableItems(filteredList);
    } catch (err) {
      if (err?.status === 403) {
        localStorage.clear();
        navigate('/Login');
      }
      console.log('Error fetching available workers: ', err);
    }
  };

  const debouncedApiCall = useCallback(
    _.debounce((searchValue, workerTypes) => {
      fetchAvailableWorkers(searchValue, workerTypes);
    }, 500), [selectedProgramApplicableFor]
  );

  const updateSearch = (searchValue) => {
    setAvailableSearch(searchValue);
    debouncedApiCall(searchValue, selectedProgramApplicableFor);
  };

  useEffect(() => {
    if (availableItems.length > 0) {
      const states = [...new Set(availableItems.map(item => item.state).filter(Boolean))];
      setStatesList(states.map(s => ({ name: s })));
    }
  }, [availableItems]);

  useEffect(() => {
    if (selectedStates.length > 0) {
      const districts = [...new Set(availableItems.filter(item => selectedStates.includes(item.state)).map(item => item.district).filter(Boolean))];
      setDistrictsList(districts.map(d => ({ name: d })));
      setSelectedDistricts(prev => prev.filter(d => districts.includes(d)));
    } else {
      setDistrictsList([]);
      setSelectedDistricts([]);
    }
  }, [selectedStates, availableItems]);

  const filteredAvailableItems = availableItems.filter(item => {
    let matchGender = true;
    if (selectedGender !== 'All') {
      matchGender = item.gender === selectedGender;
    }
    let matchState = true;
    if (selectedStates.length > 0) {
      matchState = selectedStates.includes(item.state);
    }
    let matchDistrict = true;
    if (selectedDistricts.length > 0) {
      matchDistrict = selectedDistricts.includes(item.district);
    }
    return matchGender && matchState && matchDistrict;
  }).sort((a, b) => {
    const aRemoved = initialMemberIds.includes(a.id);
    const bRemoved = initialMemberIds.includes(b.id);
    if (aRemoved && !bRemoved) return -1;
    if (!aRemoved && bRemoved) return 1;
    return 0;
  });

  const sortedSelectedItems = [...selectedItems].sort((a, b) => {
    const aAdded = !initialMemberIds.includes(a.id);
    const bAdded = !initialMemberIds.includes(b.id);
    if (aAdded && !bAdded) return -1;
    if (!aAdded && bAdded) return 1;
    return 0;
  });

  const handleDayToggle = (day) => {
    if (!isEditMode) return;
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleUpdate = (assessmentData) => {
    let isValid = true;

    if (selectedCoordinator === 'Select Coordinator *') {
      setCoordinatorError('Coordinator is required');
      isValid = false;
    } else {
      setCoordinatorError('');
    }

    if (selectedDays.length === 0) {
      setDaysError('Please select at least one day');
      isValid = false;
    } else {
      setDaysError('');
    }

    if (!isValid) {
      setStep(1);
      return;
    }

    if (isValid) {
      setLoader(true);
      let options = {
        url: `${updateTrainingBatch}/${props.editData._id || props.editData.id || props.editData.uniqueId}`,
        method: 'PUT',
        payload: {
          batchName: batchName,
          startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
          endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
          volunteerName: selectedCoordinator.split(' - ')[0],
          volunteerCN: selectedCoordinatorContact,
          members: selectedItems.map(item => item.id),
          classFrequency: selectedDays,
          ...(assessmentData?.isAssessmentRequired ? {
            assessmentConfig: {
              easyQuestions: assessmentData.easyQuestions,
              mediumQuestions: assessmentData.mediumQuestions,
              hardQuestions: assessmentData.hardQuestions,
              qualificationPercentage: assessmentData.qualificationPercentage,
              time: assessmentData.time,
              actionOnQualification: assessmentData.actionOnQualification
            }
          } : {
            assessmentConfig: null
          })
        },
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      };

      HttpService(options).then(() => {
        setLoader(false);
        props.updateFunc(false, true);
      }).catch((err) => {
        setLoader(false);
        console.error('Error updating batch: ', err);
        setApiError('Error updating batch. Please try again.');
      });
    }
  };

  return (
    <Dialog maxWidth='md' fullWidth open={props.dialogStatus} onClose={() => props.updateFunc(false)}>
        <DialogTitle style={{ backgroundColor: headingBlue, color: 'white', fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 1rem' }}>
            <Typography style={{ fontWeight: 600 }}>Batch Details</Typography>
        </DialogTitle>
        <DialogContent>
            <Box style={{ padding: '1.5rem 0rem 0rem 0rem' }}>
                {isLoader && <Loader />}
                <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <FormControlLabel
                        control={<Switch checked={isEditMode} onChange={(e) => setIsEditMode(e.target.checked)} color="primary" />}
                        label={<Typography style={{ fontSize: '0.85rem', color: headingBlue, fontWeight: 'bold' }}>Enable Edit</Typography>}
                        style={{ margin: 0 }}
                    />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem', borderBottom: `2px solid ${lightGrey}`, width: '100%' }}>
                  <Box sx={{ display: 'flex', gap: '3rem' }}>
                    {['Batch Info', 'Members', 'Assessment'].map((label, index) => {
                      const s = index + 1;
                      const isActive = step === s;
                      return (
                        <Typography
                          key={s}
                          onClick={() => setStep(s)}
                          sx={{
                            cursor: 'pointer',
                            padding: '0.2rem 0.5rem 0.5rem 0.5rem',
                            fontSize: '0.9rem',
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? headingBlue : 'gray',
                            borderBottom: isActive ? `2px solid ${headingBlue}` : '2px solid transparent',
                            marginBottom: '-2px',
                            transition: 'all 0.2s',
                            '&:hover': { color: headingBlue }
                          }}
                        >
                          {label}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                {step === 1 && (
                  <Box>
                    <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography style={{ fontSize: '0.8rem', color: headingBlue, marginBottom: '0.2rem' }}>Batch Name</Typography>
                        <CustomTextField value={batchName} setFunc={setBatchName} disabled regular={true} width="100%" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography style={{ fontSize: '0.8rem', color: headingBlue, marginBottom: '0.2rem' }}>Start Date</Typography>
                        <CustomDatePicker selectedDate={startDate} setSelectedDate={setStartDate} disabled={!isEditMode} width="100%" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography style={{ fontSize: '0.8rem', color: headingBlue, marginBottom: '0.2rem' }}>End Date</Typography>
                        <CustomDatePicker selectedDate={endDate} setSelectedDate={setEndDate} disabled={!isEditMode} width="100%" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography style={{ fontSize: '0.8rem', color: headingBlue, marginBottom: '0.2rem' }}>Program</Typography>
                        <CustomTextField value={programName} setFunc={setProgramName} disabled={true} regular={true} width="100%" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography style={{ fontSize: '0.8rem', color: headingBlue, marginBottom: '0.2rem' }}>Coordinator</Typography>
                        <CustomDropDown 
                            placeHolder='Select Coordinator *' 
                            regular={true}
                            list={coordinatorList} 
                            value={selectedCoordinator} 
                            setFunc={(val) => { 
                                setSelectedCoordinator(val.name); 
                                setSelectedCoordinatorContact(val.value); 
                            }} 
                            isValueRequired={true} 
                            width="100%" 
                            disabled={!isEditMode}
                        />
                        <Typography style={errorTextStyle}>{coordinatorError}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography style={{ color: headingBlue, fontSize: '0.9rem', width: '100%', textAlign: 'left', paddingBottom: '0.3rem' }}>Class Schedule</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', gap: '0.5rem' }}>
                            {daysOfWeek.map((day) => (
                                <Box key={day} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '10%', maxWidth: '4rem' }}>
                                    <Checkbox
                                        checked={selectedDays.includes(day)}
                                        onChange={() => handleDayToggle(day)}
                                        size="small"
                                        disabled={!isEditMode}
                                        sx={{ padding: '0.1rem', color: headingBlue }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.75rem',
                                            color: isEditMode ? headingBlue : 'grey',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            width: '100%',
                                            textAlign: 'center'
                                        }}
                                        title={day}
                                    >
                                        {day}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography style={errorTextStyle}>{daysError}</Typography>
                    </Grid>
                </Grid>
                    <Box style={{ padding: '2rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'center' }}>
                        <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.2rem 1.5rem' }} onClick={() => props.updateFunc(false)}>
                            {isEditMode ? 'Cancel' : 'Close'}
                        </Button>
                        <Button style={{ ...buttonStyle, padding: '0.2rem 1.5rem', marginLeft: '0.5rem' }} onClick={() => setStep(2)}>Next</Button>
                    </Box>
                  </Box>
                )}
                
                {step === 2 && (
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2} alignItems="center" justifyContent="center">
                        <Grid item xs={12} md={5}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                                <Typography style={{color: headingBlue, textAlign: 'left', fontWeight: 'bold', fontSize: '1rem'}}>Available Members</Typography>
                                {isEditMode && (
                                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                                        <CustomTextField placeHolder="Search..." value={availableSearch} setFunc={updateSearch} width="8rem" />
                                        <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)} size="small" style={{marginLeft: '0.5rem', padding: '2px'}}>
                                            <FilterListIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                            <Paper sx={{height: '15rem', overflowY: 'auto', overflowX: 'hidden', border: `1px solid ${mediumGrey}`, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none", opacity: isEditMode ? 1 : 0.6 }}>
                                <Box style={{display: 'flex', padding: '0.5rem', borderBottom: `1px solid ${mediumGrey}`, fontSize: '0.8rem', color: 'black'}}>
                                    <Box sx={{ flex: '0 0 35%', paddingRight: '0.5rem' }}>Name</Box>
                                    <Box sx={{ flex: '0 0 30%', paddingRight: '0.5rem' }}>Contact</Box>
                                    <Box sx={{ flex: '0 0 35%' }}>Location</Box>
                                </Box>
                                {filteredAvailableItems.map((item) => {
                                    const isRemoved = initialMemberIds.includes(item.id);
                                    const baseBg = isRemoved ? '#ffebee' : 'transparent';
                                    const hoverBg = isRemoved ? '#ffcdd2' : '#f9f9f9';
                                    return (
                                        <Box 
                                        key={item.id} 
                                        onClick={() => {
                                            if (!isEditMode) return;
                                            setActiveLeftItems(prev => 
                                                prev.find(i => i.id === item.id) 
                                                    ? prev.filter(i => i.id !== item.id) 
                                                    : [...prev, item]
                                            );
                                        }}
                                        sx={{
                                            display: 'flex',
                                            padding: '0.5rem', 
                                            cursor: isEditMode ? 'pointer' : 'default', 
                                            backgroundColor: activeLeftItems.find(i => i.id === item.id) ? '#e0e0e0' : baseBg,
                                            borderBottom: `1px solid ${isRemoved ? '#ef9a9a' : '#f0f0f0'}`,
                                            fontSize: '0.85rem',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: activeLeftItems.find(i => i.id === item.id) ? '#e0e0e0' : (isEditMode ? hoverBg : baseBg)
                                            }
                                        }}
                                    >
                                        <Box sx={{ flex: '0 0 35%', paddingRight: '0.5rem', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {item.name}
                                        </Box>
                                        <Box sx={{ flex: '0 0 30%', paddingRight: '0.5rem', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {item.contactNumber}
                                        </Box>
                                        <Box sx={{ flex: '0 0 35%', overflow: 'hidden' }} title={`${item.district || '-'}, ${item.state || '-'}`}>
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.district || '-'}</Box>
                                            <Box sx={{ fontSize: '0.7rem', color: 'gray', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.state || '-'}</Box>
                                        </Box>
                                        </Box>
                                    );
                                })}
                                {filteredAvailableItems.length === 0 && (
                                    <Typography style={{padding: '1rem', textAlign: 'center', color: 'gray', fontSize: '0.85rem'}}>No eligible members found.</Typography>
                                )}
                            </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={2} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem'}}>
                            <Button 
                                variant="contained" 
                                disabled={!isEditMode}
                                style={{backgroundColor: isEditMode ? headingBlue : 'grey', color: 'white', minWidth: '0', padding: '0.2rem 0.5rem'}}
                                onClick={() => {
                                    if(activeLeftItems.length > 0) {
                                        setSelectedItems(prev => [...prev, ...activeLeftItems]);
                                        setAvailableItems(prev => prev.filter(i => !activeLeftItems.find(ali => ali.id === i.id)));
                                        setActiveLeftItems([]);
                                    }
                                }}
                            >
                                &gt;
                            </Button>
                            <Button 
                                variant="contained" 
                                disabled={!isEditMode}
                                style={{backgroundColor: isEditMode ? headingBlue : 'grey', color: 'white', minWidth: '0', padding: '0.2rem 0.5rem'}}
                                onClick={() => {
                                    if(activeRightItems.length > 0) {
                                        setAvailableItems(prev => [...prev, ...activeRightItems]);
                                        setSelectedItems(prev => prev.filter(i => !activeRightItems.find(ari => ari.id === i.id)));
                                        setActiveRightItems([]);
                                    }
                                }}
                            >
                                &lt;
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Typography style={{color: headingBlue, marginBottom: '0.5rem', textAlign: 'left', fontWeight: 'bold', fontSize: '1rem'}}>Selected Members</Typography>
                            <Paper sx={{height: '15rem', overflowY: 'auto', overflowX: 'hidden', border: `1px solid ${mediumGrey}`, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none"}}>
                                <Box style={{display: 'flex', padding: '0.5rem', borderBottom: `1px solid ${mediumGrey}`, fontSize: '0.8rem', color: 'black'}}>
                                    <Box sx={{ flex: '0 0 35%', paddingRight: '0.5rem' }}>Name</Box>
                                    <Box sx={{ flex: '0 0 30%', paddingRight: '0.5rem' }}>Contact</Box>
                                    <Box sx={{ flex: '0 0 35%' }}>Location</Box>
                                </Box>
                                {sortedSelectedItems.map((item) => {
                                    const isNewlyAdded = !initialMemberIds.includes(item.id);
                                    const baseBg = isNewlyAdded ? '#e8f5e9' : 'transparent';
                                    const hoverBg = isNewlyAdded ? '#c8e6c9' : '#f9f9f9';
                                    return (
                                        <Box 
                                        key={item.id} 
                                        onClick={() => {
                                            if (!isEditMode) return;
                                            setActiveRightItems(prev => 
                                                prev.find(i => i.id === item.id) 
                                                    ? prev.filter(i => i.id !== item.id) 
                                                    : [...prev, item]
                                            );
                                        }}
                                        sx={{
                                            display: 'flex',
                                            padding: '0.5rem', 
                                            cursor: isEditMode ? 'pointer' : 'default', 
                                            backgroundColor: activeRightItems.find(i => i.id === item.id) ? '#e0e0e0' : baseBg,
                                            borderBottom: `1px solid ${isNewlyAdded ? '#a5d6a7' : '#f0f0f0'}`,
                                            fontSize: '0.85rem',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: activeRightItems.find(i => i.id === item.id) ? '#e0e0e0' : (isEditMode ? hoverBg : baseBg)
                                            }
                                        }}
                                    >
                                        <Box sx={{ flex: '0 0 35%', paddingRight: '0.5rem', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {item.name}
                                        </Box>
                                        <Box sx={{ flex: '0 0 30%', paddingRight: '0.5rem', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {item.contactNumber}
                                        </Box>
                                        <Box sx={{ flex: '0 0 35%', overflow: 'hidden' }} title={`${item.district || '-'}, ${item.state || '-'}`}>
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.district || '-'}</Box>
                                            <Box sx={{ fontSize: '0.7rem', color: 'gray', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.state || '-'}</Box>
                                        </Box>
                                        </Box>
                                    );
                                })}
                                {sortedSelectedItems.length === 0 && (
                                    <Typography style={{padding: '1rem', textAlign: 'center', color: 'gray', fontSize: '0.85rem'}}>No members selected.</Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                    <Box style={{ padding: '2rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'center' }}>
                        <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.2rem 1.5rem' }} onClick={() => setStep(1)}>Back</Button>
                        <Button style={{ ...buttonStyle, padding: '0.2rem 1.5rem', marginLeft: '0.5rem' }} onClick={() => setStep(3)}>Next</Button>
                    </Box>
                  </Box>
                )}

                {step === 3 && (
                    <Box sx={{ mt: 1 }}>
                        <AssessmentConfiguration 
                            key={props.editData?._id || 'edit'}
                            onBack={() => setStep(2)} 
                            onSubmit={handleUpdate} 
                            initialValues={assessmentInitialValues}
                            readOnly={!isEditMode}
                            submitText="Update Batch"
                        />
                    </Box>
                )}

                {apiError && <Typography style={{ color: red, fontSize: '0.85rem', textAlign: 'center', marginTop: '1rem' }}>{apiError}</Typography>}

                <Popover
                    open={Boolean(filterAnchorEl)}
                    anchorEl={filterAnchorEl}
                    onClose={() => setFilterAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: '1rem', width: '16rem' }}>
                        <Typography variant="subtitle2" style={{color: headingBlue, fontWeight: 'bold'}}>Filters</Typography>
                        <CustomDropDown
                            regular={true}
                            placeHolder="Gender"
                            list={[{name: 'All'}, {name: 'Male'}, {name: 'Female'}]}
                            value={selectedGender}
                            setFunc={(val) => setSelectedGender(val.name)}
                        />
                        <MultiSelectDropDown
                            label="States"
                            list={statesList}
                            optionName={selectedStates}
                            setOptionName={setSelectedStates}
                        />
                        <MultiSelectDropDown
                            label="Districts"
                            list={districtsList}
                            optionName={selectedDistricts}
                            setOptionName={setSelectedDistricts}
                        />
                        <Box style={{display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem'}}>
                            <Button onClick={() => { setSelectedGender('All'); setSelectedStates([]); setSelectedDistricts([]); }} size="small" style={{...buttonStyle, backgroundColor: 'white', color: headingBlue, border: `1px solid ${headingBlue}`, marginRight: '0.5rem'}}>Clear</Button>
                            <Button onClick={() => setFilterAnchorEl(null)} size="small" style={buttonStyle}>Apply</Button>
                        </Box>
                    </Box>
                </Popover>

            </Box>
        </DialogContent>
    </Dialog>
  );
};

export default EditBatchDialog;